import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  ArrowRight,
  Shield,
  Zap,
  BarChart3,
  Mail,
  AlertTriangle,
  Globe,
  Sparkles,
  Brain,
  Inbox,
  Flame,
  Gauge,
  Radar,
  Network,
  Bot,
  CheckCircle2,
  TrendingUp,
  Lock,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "InboxPulse — KI-gestütztes Cold-Email Deliverability Monitoring" },
      {
        name: "description",
        content:
          "Enterprise Deliverability-Plattform mit KI: Inbox-Placement, Reputation, Bounce-Tracking, Warmup & smarter Assistant. 1.000 €/Monat — alles inklusive.",
      },
    ],
  }),
  component: Landing,
});

function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="border-b border-border/60 backdrop-blur sticky top-0 z-50 bg-background/70">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="size-8 rounded-lg bg-[image:var(--gradient-primary)] flex items-center justify-center shadow-[var(--shadow-glow)]">
              <Activity className="size-4 text-primary-foreground" />
            </div>
            <span className="font-semibold tracking-tight">InboxPulse</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition">
              Features
            </a>
            <a href="#tools" className="hover:text-foreground transition">
              Tools
            </a>
            <a href="#ai" className="hover:text-foreground transition">
              KI
            </a>
            <a href="#pricing" className="hover:text-foreground transition">
              Preis
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:text-foreground transition"
            >
              Sign in
            </Link>
            <Link to="/login">
              <Button size="sm" className="bg-[image:var(--gradient-primary)] hover:opacity-90">
                Jetzt starten
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary)/0.18,_transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-20 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground mb-6">
              <Sparkles className="size-3 text-primary" />
              Jetzt mit KI-Assistent & Auto-Optimierung
            </div>
            <h1 className="text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
              Wisse exakt, wo deine
              <br />
              <span className="bg-[image:var(--gradient-primary)] bg-clip-text text-transparent">
                Cold Emails landen.
              </span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Enterprise-Deliverability-Plattform mit KI-Unterstützung. Überwache Inbox-Placement,
              Reputation, Bounces und Provider-Events in Echtzeit — über hunderte Domains hinweg.
              Inklusive smarter Empfehlungen, Auto-Warmup und Spam-Analyzer.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button
                  size="lg"
                  className="bg-[image:var(--gradient-primary)] hover:opacity-90 shadow-[var(--shadow-glow)]"
                >
                  Jetzt starten <ArrowRight className="ml-1 size-4" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button size="lg" variant="outline">
                  Preis ansehen
                </Button>
              </a>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-success" /> DSGVO-konform
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-success" /> Hosting in der EU
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-success" /> Keine Setup-Gebühr
              </span>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "300+", v: "Domains überwacht" },
              { k: "12M", v: "Events / Tag" },
              { k: "<1s", v: "Pixel-Latenz" },
              { k: "98%", v: "Inbox-Genauigkeit" },
            ].map((s) => (
              <div key={s.v} className="rounded-xl border border-border bg-card/50 p-5">
                <div className="text-3xl font-semibold tracking-tight">{s.k}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section id="ai" className="relative border-y border-border/60 bg-card/20">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs text-primary mb-5">
              <Brain className="size-3.5" /> KI-Engine
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Deine KI für maximale Zustellbarkeit.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Der integrierte KI-Assistent analysiert jede Kampagne, erkennt Risikomuster und
              schlägt konkrete Optimierungen vor — vom Betreff bis zum Sendezeitpunkt. Lernt
              kontinuierlich aus deinen Daten.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Spam-Score-Vorhersage vor dem Versand",
                "Smarte Betreff- & Snippet-Vorschläge",
                "Anomalie-Erkennung bei Bounce-Spikes",
                "Auto-Pause bei Reputationsverlust",
                "KI-Chat zur Diagnose deiner Domains",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-success mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-2xl border border-border bg-card/60 p-6 shadow-[var(--shadow-glow)]">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
              <Bot className="size-4 text-primary" /> InboxPulse Assistant
            </div>
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-background/60 p-3 border border-border/60">
                <span className="text-muted-foreground">Du:</span> Warum sinkt meine Open-Rate auf
                Gmail?
              </div>
              <div className="rounded-lg bg-primary/10 p-3 border border-primary/30">
                <span className="text-primary font-medium">KI:</span> 3 Sender zeigen erhöhte
                Spam-Quote. Empfehle: Warmup auf 40 % zurückfahren, Link-Domain rotieren, Betreff
                verkürzen.
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-success">
                  <TrendingUp className="size-3" /> +18 % erwartet
                </span>
              </div>
              <div className="rounded-lg bg-background/60 p-3 border border-border/60 text-muted-foreground text-xs">
                Analyse in 0,8 s • 12 Datenquellen
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl">
          Alles, was du brauchst — in einer Plattform.
        </h2>
        <p className="mt-4 text-muted-foreground max-w-2xl">
          Über 25 Module für Monitoring, Versand, Warmup und Analyse — alle nahtlos integriert.
        </p>
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {[
            {
              i: Brain,
              t: "KI-Assistent",
              d: "Chat-basierte Diagnose, Empfehlungen und automatische Optimierungs-Playbooks.",
            },
            {
              i: Shield,
              t: "Reputations-Engine",
              d: "Echtzeit-Domain-Reputation & Blacklist-Monitoring (Spamhaus, Barracuda, Spamcop).",
            },
            {
              i: Zap,
              t: "Live Tracking-Pixel",
              d: "Pro-Kampagne-Pixel. Erfasst Opens, Provider, Device, Geo & Timestamp.",
            },
            {
              i: BarChart3,
              t: "Provider-Heatmaps",
              d: "Inbox vs. Spam-Placement bei Gmail, Outlook, Yahoo, GMX, T-Online.",
            },
            {
              i: Mail,
              t: "HTML Spam-Analyzer",
              d: "Templates hochladen → Spam-Trigger, Link-Audit, Bild/Text-Verhältnis.",
            },
            {
              i: AlertTriangle,
              t: "Automatische Alerts",
              d: "Sender pausieren bei Spam-Spikes. Slack, Telegram & E-Mail-Benachrichtigungen.",
            },
            {
              i: Globe,
              t: "Webhooks ready",
              d: "Mailgun, SendGrid, SES, Postmark — Bounce- & Complaint-Events out-of-the-box.",
            },
            {
              i: Flame,
              t: "Auto-Warmup",
              d: "Intelligenter Warmup-Scheduler für neue Domains und Mailboxen.",
            },
            {
              i: Inbox,
              t: "Seed-Inboxen",
              d: "Über 50 Seed-Accounts liefern echte Placement-Daten pro Provider.",
            },
          ].map((f) => (
            <div
              key={f.t}
              className="group rounded-xl border border-border bg-card/40 p-6 hover:bg-card/70 transition"
            >
              <div className="size-10 rounded-lg bg-accent/40 flex items-center justify-center mb-4 group-hover:bg-accent/70 transition">
                <f.i className="size-5 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">{f.t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tools catalog */}
      <section id="tools" className="border-t border-border/60 bg-card/20">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">
            Über 25 integrierte Tools — alle inklusive.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            Eine vollständige Suite für Cold-Email-Profis. Kein Aufpreis, keine Add-ons.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {[
              {
                cat: "Monitoring & Health",
                icon: Gauge,
                tools: [
                  "Live-Health Dashboard",
                  "Queue Health",
                  "Dead-Letter Queue",
                  "Domain-Check Jobs & Stats",
                  "Metrics & Trends",
                  "OAuth-Diagnose",
                ],
              },
              {
                cat: "Kampagnen & Versand",
                icon: Send,
                tools: [
                  "Campaign Wizard",
                  "SMTP-Send",
                  "Sender-Pool & Rotation",
                  "Segmente",
                  "Recipient-Import",
                  "A/B-Test",
                  "Perfect-Snippet Generator",
                ],
              },
              {
                cat: "Placement & Tracking",
                icon: Radar,
                tools: [
                  "Gmail-Placement-Tracker",
                  "Provider-Heatmaps",
                  "Campaign-Pixel-Hits",
                  "Open-Verifizierung",
                  "Pixel-Debug",
                  "Placement-Rules",
                ],
              },
              {
                cat: "Bounce & Reputation",
                icon: Shield,
                tools: [
                  "Bounce-Monitor",
                  "Bounce-Log",
                  "Campaign-Bounces",
                  "VERP-Bulk",
                  "Bounce-Inbox Einstellungen",
                  "Duplicate-Incidents",
                ],
              },
              {
                cat: "Alerts & Automation",
                icon: AlertTriangle,
                tools: [
                  "Alert-Rules",
                  "Domain-Alert-Rules",
                  "Globale Alert-Settings",
                  "Telegram-Log",
                  "Drain-Settings",
                  "Auto-Pause",
                ],
              },
              {
                cat: "KI & Infrastruktur",
                icon: Network,
                tools: [
                  "KI-Assistent (Chat)",
                  "HTML-Analyzer",
                  "Warmup-Scheduler",
                  "Seed-Inboxen",
                  "Domains-Manager",
                  "Webhook-Integrationen",
                ],
              },
            ].map((g) => (
              <div key={g.cat} className="rounded-xl border border-border bg-card/40 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="size-9 rounded-lg bg-accent/40 flex items-center justify-center">
                    <g.icon className="size-4 text-primary" />
                  </div>
                  <h3 className="font-semibold">{g.cat}</h3>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {g.tools.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <CheckCircle2 className="size-3.5 text-success mt-0.5 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs text-primary mb-5">
            <Lock className="size-3.5" /> Ein Preis. Alles drin.
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Einfaches Pricing für ernsthafte Sender.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Keine Staffeln, keine versteckten Limits — du bekommst die komplette Plattform inkl. KI.
          </p>
        </div>

        <div className="mt-12 max-w-xl mx-auto rounded-2xl border border-primary/40 bg-[image:var(--gradient-primary)]/5 p-8 shadow-[var(--shadow-glow)] relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary)/0.15,_transparent_70%)] pointer-events-none" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-primary font-medium">Professional</div>
                <div className="text-xs text-muted-foreground mt-1">Monatlich kündbar</div>
              </div>
              <Sparkles className="size-5 text-primary" />
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="text-5xl md:text-6xl font-semibold tracking-tight">1.000 €</span>
              <span className="text-muted-foreground">/ Monat</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">zzgl. MwSt. — keine Setup-Gebühr</p>

            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Unbegrenzte Domains & Sender",
                "KI-Assistent mit unbegrenzten Anfragen",
                "Alle 25+ Tools & Module inklusive",
                "Auto-Warmup für alle Mailboxen",
                "Echtzeit-Alerts (Slack, Telegram, E-Mail)",
                "Premium-Support & Onboarding",
                "DSGVO-konformes EU-Hosting",
                "API & Webhooks ohne Limit",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-success mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <Link to="/login" className="block mt-8">
              <Button
                size="lg"
                className="w-full bg-[image:var(--gradient-primary)] hover:opacity-90 shadow-[var(--shadow-glow)]"
              >
                Abo starten <ArrowRight className="ml-1 size-4" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Jederzeit zum Monatsende kündbar. Keine Mindestlaufzeit.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/60 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-muted-foreground">
          <div>© InboxPulse</div>
          <Link to="/login" className="hover:text-foreground">
            Sign in
          </Link>
        </div>
      </footer>
    </div>
  );
}
